import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI, caisseAPI } from '../../services/api';
import BalanceCard from './BalanceCard';
import CustomSnackbar from '../../components/ui/Snackbar';
import Rows from '../../components/ui/Rows';
import CaisseSelector from '../../components/ui/CaisseSelector';
import DateRangeFilter from '../../components/ui/DateRangeFilter';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import { formatAmountForDisplay } from '../../utils/amountFormatter';

const Dashboard = () => {
  const { user } = useAuth();
  const history = useHistory();
  const initialCaisseIdsFromUrl = useRef(
    new URLSearchParams(window.location.search)
      .get('caisseIds')
      ?.split(',').map(Number).filter(Boolean) ?? null
  );
  const isInitialized = useRef(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCaisseIds, setSelectedCaisseIds] = useState([]);
  const [caisseOptions, setCaisseOptions] = useState([]);
  const [physiqueCaisses, setPhysiqueCaisses] = useState([]);
  const [mobileCaisses, setMobileCaisses] = useState([]);
  const [caisseBalances, setCaisseBalances] = useState({ caisse: 0, coffre: 0 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Lists data state
  const [categoriesData, setCategoriesData] = useState([]);
  const [coffreTransactionsData, setCoffreTransactionsData] = useState([]);
  
  // Unified date range for both lists — initialized from URL if present
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const fromDate = params.get('fromDate');
    const toDate = params.get('toDate');
    if (fromDate && toDate) {
      return { start: new Date(fromDate + 'T00:00:00'), end: new Date(toDate + 'T00:00:00') };
    }
    return null;
  });
  
  // State for sorting
  const [categoriesSortBy, setCategoriesSortBy] = useState('categoryName');
  const [categoriesSortDirection, setCategoriesSortDirection] = useState('ASC');
  const [coffreSortBy, setCoffreSortBy] = useState('createdAt');
  const [coffreSortDirection, setCoffreSortDirection] = useState('DESC');
  const [commercialSortBy, setCommercialSortBy] = useState('commercial');
  const [commercialSortDirection, setCommercialSortDirection] = useState('ASC');
  
  // Function to get caisse-specific data from the already loaded data
  const getCaisseData = useCallback((caisseId) => {
    if (!dashboardData?.caisses) return { 
      caisse: 0, 
      coffre: 0
    };
    
    const selectedCaisseData = dashboardData.caisses.find(caisse => caisse.id === caisseId);
    
    if (!selectedCaisseData) return { 
      caisse: 0, 
      coffre: 0
    };
    
    return {
      caisse: selectedCaisseData.caisseBalance || 0,
      coffre: selectedCaisseData.coffreBalance || 0
    };
  }, [dashboardData]);

  // Fetch categories data
  const fetchCategoriesData = useCallback(async (caisseIds, dateRange) => {
    if (!caisseIds || (Array.isArray(caisseIds) && caisseIds.length === 0)) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      let response;
      if (Array.isArray(caisseIds) && caisseIds.length > 1) {
        // Multiple caisses
        response = await dashboardAPI.getMultipleCaissesCategoriesSummary(caisseIds, fromDate, toDate, categoriesSortBy, categoriesSortDirection);
      } else {
        // Single caisse
        const caisseId = Array.isArray(caisseIds) ? caisseIds[0] : caisseIds;
        response = await dashboardAPI.getCategoriesSummary(caisseId, fromDate, toDate, categoriesSortBy, categoriesSortDirection);
      }
      
      const categories = response.data.categories || [];
      
      // Transform data for display
      const transformedData = categories.map(category => {
        const isMixed = category.caisseFactor !== 0 && category.coffreFactor !== 0;
        const isNeutral = category.caisseFactor === 0 && category.coffreFactor === 0;
        
        let amountDisplay;
        let amountColor;
        
        // Backend sends effective amounts (already multiplied by factor)
        if (isMixed || isNeutral) {
          // Mixed or neutral transactions - no sign
          amountDisplay = formatAmountForDisplay(category.sum, false).text;
          amountColor = '#027BC6'; // Blue for neutral/mixed
        } else {
          // Pure transactions - effective amount with sign
          const formatted = formatAmountForDisplay(category.sum, true);
          amountDisplay = formatted.text;
          amountColor = formatted.color;
        }
        
        return {
          categoryName: category.categoryName,
          sum: amountDisplay,
          isMixed,
          isNeutral,
          amountColor,
          categoryDetailIds: category.categoryDetailIds || []
        };
      });
      
      setCategoriesData(transformedData);
    } catch (error) {
      console.error('Error fetching categories data:', error);
      setCategoriesData([]);
    }
  }, [categoriesSortBy, categoriesSortDirection]);

  // Fetch coffre transactions data
  const fetchCoffreTransactionsData = useCallback(async (caisseIds, dateRange) => {
    if (!caisseIds || (Array.isArray(caisseIds) && caisseIds.length === 0)) return;
    
    try {
      const fromDate = dateRange?.start ? dateRange.start.toLocaleDateString('en-CA') : null;
      const toDate = dateRange?.end ? dateRange.end.toLocaleDateString('en-CA') : null;
      
      // Handle both single caisseId and array of caisseIds
      const ids = Array.isArray(caisseIds) ? caisseIds : [caisseIds];
      
      // Fetch transactions for all selected caisses
      const allTransactions = [];
      for (const caisseId of ids) {
        try {
          const response = await dashboardAPI.getCoffreTransactions(caisseId, fromDate, toDate, coffreSortBy, coffreSortDirection);
          const transactions = response.data || [];
          allTransactions.push(...transactions);
        } catch (error) {
          console.error(`Error fetching coffre transactions for caisse ${caisseId}:`, error);
        }
      }
      
      const dir = coffreSortDirection === 'ASC' ? 1 : -1;
      allTransactions.sort((a, b) => {
        if (coffreSortBy === 'amount') {
          return dir * ((a.amount * (a.factor || 1)) - (b.amount * (b.factor || 1)));
        } else if (coffreSortBy === 'agentName') {
          return dir * (a.by || '').localeCompare(b.by || '');
        } else {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dir * (dateA - dateB);
        }
      });

      const last10Transactions = allTransactions.slice(0, 10);
      
      // Transform data for display
      const transformedData = last10Transactions.map(transaction => {
        const factor = transaction.factor || 1;
        const adjustedAmount = transaction.amount * factor;
        const formatted = formatAmountForDisplay(adjustedAmount, true);
        
        return {
          createdAt: transaction.date,
          amount: formatted.text,
          amountColor: formatted.color,
          agentName: transaction.by
        };
      });
      
      setCoffreTransactionsData(transformedData);
    } catch (error) {
      console.error('Error fetching coffre transactions data:', error);
      setCoffreTransactionsData([]);
    }
  }, [coffreSortBy, coffreSortDirection]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (user.role === 'AGENT') {
          const assigned = user.assignedCaisses && user.assignedCaisses[0] ? user.assignedCaisses[0] : null;
          if (assigned) {
            const option = { id: assigned.id, name: assigned.name, type: assigned.type };
            setCaisseOptions([option]);
            setSelectedCaisseIds([assigned.id]);
            
            const resp = await dashboardAPI.getCaisseDashboard(assigned.id);
            const data = resp.data || {};
            setDashboardData({ caisses: [{ id: assigned.id, name: assigned.name, caisseBalance: data.caisseBalance || 0, coffreBalance: data.coffreBalance || 0 }] });
          }
        } else {
          const response = await caisseAPI.getForFilters(true);
          const caisses = Array.isArray(response.data) ? response.data : [];
          
          const physiques = caisses
            .filter(c => c.type === 'PHYSIQUE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'PHYSIQUE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          const mobiles = caisses
            .filter(c => c.type === 'MOBILE')
            .map(caisse => ({ id: caisse.id, name: caisse.name, type: 'MOBILE' }))
            .sort((a, b) => a.name.localeCompare(b.name));
          
          setPhysiqueCaisses(physiques);
          setMobileCaisses(mobiles);
          
          const allCaisses = [...physiques, ...mobiles];
          setCaisseOptions(allCaisses);
          
          const urlCaisseIds = initialCaisseIdsFromUrl.current;
          let initialSelectedIds;
          if (urlCaisseIds && urlCaisseIds.length > 0) {
            const validIds = urlCaisseIds.filter(id => allCaisses.some(c => c.id === id));
            initialSelectedIds = validIds.length > 0 ? validIds : [];
          } else {
            initialSelectedIds = [];
          }
          if (initialSelectedIds.length > 0) {
            setSelectedCaisseIds(initialSelectedIds);
            const dashboardResponse = await dashboardAPI.getAllCaissesDashboard(null, initialSelectedIds);
            setDashboardData(dashboardResponse.data);
          }
        }
        
        setLoading(false);
        isInitialized.current = true;
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Échec du chargement des données du tableau de bord');
        setSnackbar({
          open: true,
          message: 'Erreur lors du chargement des données du tableau de bord',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Load dashboard data when selected caisses change
  useEffect(() => {
    const loadDashboardData = async () => {
      if (user?.role === 'AGENT') {
        if (selectedCaisseIds.length > 0) {
          const caisseId = selectedCaisseIds[0];
          try {
            const resp = await dashboardAPI.getCaisseDashboard(caisseId);
            const data = resp.data || {};
            const assigned = user.assignedCaisses?.[0];
            setDashboardData({ 
              caisses: [{ 
                id: caisseId, 
                name: assigned?.name || '', 
                caisseBalance: data.caisseBalance || 0, 
                coffreBalance: data.coffreBalance || 0 
              }] 
            });
          } catch (err) {
            console.error('Error loading dashboard data:', err);
          }
        }
      } else if (user?.role === 'MANAGER' && selectedCaisseIds.length > 0) {
        try {
          const dashboardResponse = await dashboardAPI.getAllCaissesDashboard(null, selectedCaisseIds);
          setDashboardData(dashboardResponse.data);
        } catch (err) {
          console.error('Error loading dashboard data:', err);
        }
      }
    };

    if (user && selectedCaisseIds.length > 0) {
      loadDashboardData();
    } else if (user && selectedCaisseIds.length === 0) {
      setDashboardData(null);
      setCaisseBalances({ caisse: 0, coffre: 0 });
    }
  }, [user, selectedCaisseIds]);

  // Update caisse balances when dashboard data changes
  useEffect(() => {
    if (dashboardData?.caisses && selectedCaisseIds.length > 0) {
      if (selectedCaisseIds.length === 1) {
        const caisseData = getCaisseData(selectedCaisseIds[0]);
        setCaisseBalances({
          caisse: caisseData.caisse,
          coffre: caisseData.coffre
        });
      } else {
        let totalCaisse = 0;
        let totalCoffre = 0;
        selectedCaisseIds.forEach(id => {
          const data = getCaisseData(id);
          totalCaisse += data.caisse;
          totalCoffre += data.coffre;
        });
        setCaisseBalances({
          caisse: totalCaisse,
          coffre: totalCoffre
        });
      }
    }
  }, [dashboardData, selectedCaisseIds, getCaisseData]);

  const isVerificationCaisse = user?.role === 'AGENT' && (
    user.assignedCaisses?.[0]?.isDashboardCaisse === false ||
    user.assignedCaisses?.[0]?.name === 'Vérification'
  );

  // Check if multiple mobile caisses are selected
  const isMultipleMobileCaissesSelected = useMemo(() => {
    if (user?.role !== 'MANAGER' || selectedCaisseIds.length === 0 || mobileCaisses.length === 0) {
      return false;
    }
    const selectedMobileCount = selectedCaisseIds.filter(id => 
      mobileCaisses.some(c => c.id === id)
    ).length;
    return selectedMobileCount > 1;
  }, [selectedCaisseIds, mobileCaisses, user?.role]);

  // Sync selected filters to URL (replace, not push — browser back goes to previous page, not previous filter)
  useEffect(() => {
    if (!isInitialized.current) return;
    const params = new URLSearchParams();
    if (selectedCaisseIds.length > 0) {
      params.set('caisseIds', selectedCaisseIds.join(','));
    }
    if (dateRange?.start && dateRange?.end) {
      params.set('fromDate', dateRange.start.toLocaleDateString('en-CA'));
      params.set('toDate', dateRange.end.toLocaleDateString('en-CA'));
    }
    history.replace({ search: params.toString() });
  }, [selectedCaisseIds, dateRange, history]);

  // Fetch lists data when caisse or date range changes
  useEffect(() => {
    if (selectedCaisseIds.length > 0) {
      fetchCategoriesData(selectedCaisseIds, dateRange);
      fetchCoffreTransactionsData(selectedCaisseIds, dateRange);
    } else {
      setCategoriesData([]);
      setCoffreTransactionsData([]);
    }
  }, [selectedCaisseIds, dateRange, categoriesSortBy, categoriesSortDirection, coffreSortBy, coffreSortDirection, fetchCategoriesData, fetchCoffreTransactionsData, isMultipleMobileCaissesSelected]);

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  // Header height is approximately 76px, so Dashboard sticky header starts below it
  const HEADER_HEIGHT = 76;

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  return (
    <Box>
      {/* Sticky Header Section - Always render to prevent line during loading */}
      <Box
        sx={{
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          zIndex: 999,
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(248, 248, 248, 1), #F8F8F8)'
        }}
      >
        <Box sx={{ ...contentContainerSx, paddingTop: '24px', paddingBottom: '1px' }}>
          <Box sx={{ marginBottom: '8px', width: '100%' }}>
            <h1 
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '32px',
                letterSpacing: '0px',
                color: '#38485C',
                opacity: 0.88,
                textTransform: 'none',
                margin: 0
              }}
            >
              Tableau de Bord
            </h1>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
              marginBottom: '24px'
            }}
          >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {user.role === 'AGENT' ? (
              <Typography style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '18px',
                color: '#027BC6',
              }}>
                {caisseOptions.length > 0 ? caisseOptions[0].name : ''}
              </Typography>
            ) : (
              <CaisseSelector
                caisses={caisseOptions}
                selectedIds={selectedCaisseIds}
                onChange={setSelectedCaisseIds}
              />
            )}
          </Box>
          {user?.role !== 'AGENT' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0, alignSelf: 'flex-start' }}>
              <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />
              {(selectedCaisseIds.length > 0 || dateRange !== null) && (
                <span
                  onClick={() => { setSelectedCaisseIds([]); setDateRange(null); }}
                  style={{
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#007BC6',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Réinitialiser
                </span>
              )}
            </Box>
          )}
        </Box>

          {!isVerificationCaisse && (
            <Box
              display="flex"
              gap={3}
              mb={5}
              sx={{ width: '100%', flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: 1, minWidth: '240px' }}>
                <BalanceCard
                  title="Solde Caisse"
                  amount={formatAmountForDisplay(caisseBalances.caisse, false).text}
                  color="blue"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '240px' }}>
                <BalanceCard
                  title="Solde Coffre"
                  amount={formatAmountForDisplay(caisseBalances.coffre, false).text}
                  color="green"
                />
              </Box>
            </Box>
          )}

          {/* Table Titles and Headers */}
          <Box 
            display="flex" 
            gap={4} 
            mb={2}
            sx={{ width: '100%', flexWrap: 'wrap' }}
          >
            {/* Left Table Header */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}
              >
                <Typography
                  style={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '32px',
                    letterSpacing: '0px',
                    color: '#38485C',
                    opacity: 0.88,
                    textTransform: 'none',
                    margin: 0
                  }}
                >
                  {isMultipleMobileCaissesSelected ? 'Solde par commercial' : 'Sommes par catégorie'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  color: '#919191',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  backgroundColor: '#F8F8F8',
                  marginTop: '0px'
                }}
              >
                {isMultipleMobileCaissesSelected ? (
                  <>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (commercialSortBy === 'commercial' && commercialSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCommercialSortBy('commercial');
                      setCommercialSortDirection(newDirection);
                    }}>
                      <span>Commercial</span>
                      {commercialSortBy === 'commercial' && (
                        <img 
                          src={chevronUpIcon}
                          alt="sort"
                          style={{ marginLeft: '4px', width: '12px', height: '12px', transform: commercialSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (commercialSortBy === 'solde' && commercialSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCommercialSortBy('solde');
                      setCommercialSortDirection(newDirection);
                    }}>
                      <span>Solde</span>
                      {commercialSortBy === 'solde' && (
                        <img 
                          src={chevronUpIcon}
                          alt="sort"
                          style={{ marginLeft: '4px', width: '12px', height: '12px', transform: commercialSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (categoriesSortBy === 'categoryName' && categoriesSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCategoriesSortBy('categoryName');
                      setCategoriesSortDirection(newDirection);
                    }}>
                      <span>Catégories</span>
                      {categoriesSortBy === 'categoryName' && (
                        <img 
                          src={chevronUpIcon}
                          alt="sort"
                          style={{ marginLeft: '4px', width: '12px', height: '12px', transform: categoriesSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                      let newDirection = 'ASC';
                      if (categoriesSortBy === 'sum' && categoriesSortDirection === 'ASC') {
                        newDirection = 'DESC';
                      }
                      setCategoriesSortBy('sum');
                      setCategoriesSortDirection(newDirection);
                    }}>
                      <span>Somme</span>
                      {categoriesSortBy === 'sum' && (
                        <img 
                          src={chevronUpIcon}
                          alt="sort"
                          style={{ marginLeft: '4px', width: '12px', height: '12px', transform: categoriesSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {/* Right Table Header */}
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}
              >
                <Typography
                  style={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '24px',
                    lineHeight: '32px',
                    letterSpacing: '0px',
                    color: '#38485C',
                    opacity: 0.88,
                    textTransform: 'none',
                    margin: 0
                  }}
                >
                  Entrées/Sorties Coffre
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  color: '#919191',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  backgroundColor: '#F8F8F8',
                  marginTop: '0px',
                  marginBottom: '0px'
                }}
              >
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'createdAt' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('createdAt');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Date</span>
                  {coffreSortBy === 'createdAt' && (
                    <img 
                      src={chevronUpIcon}
                      alt="sort"
                      style={{ marginLeft: '4px', width: '12px', height: '12px', transform: coffreSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                    />
                  )}
                </Box>
                <Box sx={{ flexShrink: 0, width: '200px', display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'amount' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('amount');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Montant</span>
                  {coffreSortBy === 'amount' && (
                    <img 
                      src={chevronUpIcon}
                      alt="sort"
                      style={{ marginLeft: '4px', width: '12px', height: '12px', transform: coffreSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: '24px' }} onClick={() => {
                  let newDirection = 'ASC';
                  if (coffreSortBy === 'agentName' && coffreSortDirection === 'ASC') {
                    newDirection = 'DESC';
                  }
                  setCoffreSortBy('agentName');
                  setCoffreSortDirection(newDirection);
                }}>
                  <span>Par</span>
                  {coffreSortBy === 'agentName' && (
                    <img 
                      src={chevronUpIcon}
                      alt="sort"
                      style={{ marginLeft: '4px', width: '12px', height: '12px', transform: coffreSortDirection === 'DESC' ? 'rotate(180deg)' : 'none' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ ...contentContainerSx, paddingTop: '32px', paddingBottom: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box 
          display="flex" 
          gap={4} 
          mb={6}
          sx={{ width: '100%', flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            {isMultipleMobileCaissesSelected ? (
              <Rows
                title=""
                hideHeader={true}
                columns={[
                  { key: 'commercial', label: 'Commercial', sortable: true },
                  { 
                    key: 'solde', 
                    label: 'Solde',
                    sortable: true,
                    render: (amount) => (
                      <span style={{ 
                        color: '#027BC6',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        lineHeight: '19px',
                        letterSpacing: '0px',
                        textTransform: 'none',
                        opacity: 1
                      }}>
                        {amount}
                      </span>
                    )
                  }
                ]}
                data={(() => {
                  if (!dashboardData?.caisses) return [];
                  const commercialData = dashboardData.caisses
                    .filter(caisse => mobileCaisses.some(mc => mc.id === caisse.id))
                    .map(caisse => ({
                      commercial: caisse.name,
                      solde: formatAmountForDisplay(caisse.caisseBalance || 0, false).text,
                      soldeValue: typeof caisse.caisseBalance === 'number' ? caisse.caisseBalance : parseFloat(caisse.caisseBalance || 0)
                    }));
                  
                  return commercialData.sort((a, b) => {
                    let comparison = 0;
                    if (commercialSortBy === 'commercial') {
                      comparison = a.commercial.localeCompare(b.commercial);
                    } else if (commercialSortBy === 'solde') {
                      comparison = a.soldeValue - b.soldeValue;
                    }
                    return commercialSortDirection === 'ASC' ? comparison : -comparison;
                  });
                })()}
                maxHeight="500px"
                sortBy={commercialSortBy}
                sortDirection={commercialSortDirection}
                onSort={(sortBy, sortDirection) => {
                  setCommercialSortBy(sortBy);
                  setCommercialSortDirection(sortDirection);
                }}
                containerPadding={0}
              />
            ) : (
              <Rows
                title=" "
                hideHeader={true}
                columns={[
                  { 
                    key: 'categoryName', 
                    label: 'Catégories', 
                    sortable: true
                  },
                  { 
                    key: 'sum', 
                    label: 'Somme',
                    sortable: true,
                    render: (amount, row) => (
                      <span style={{ 
                        color: row.amountColor,
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        lineHeight: '19px',
                        letterSpacing: '0px',
                        textTransform: 'none',
                        opacity: 1
                      }}>
                        {amount}
                      </span>
                    )
                  }
                ]}
                data={categoriesData}
                maxHeight="500px"
                sortBy={categoriesSortBy}
                sortDirection={categoriesSortDirection}
                onSort={(sortBy, sortDirection) => {
                  setCategoriesSortBy(sortBy);
                  setCategoriesSortDirection(sortDirection);
                }}
                onRowClick={(row) => {
                  if (row.categoryDetailIds && row.categoryDetailIds.length > 0) {
                    // Build query params for Transactions page
                    const params = new URLSearchParams();
                    params.set('categoryDetailIds', row.categoryDetailIds.join(','));
                    
                    // Add selected caisse IDs
                    if (selectedCaisseIds.length > 0) {
                      params.set('caisseIds', selectedCaisseIds.join(','));
                    } else if (selectedCaisseIds.length === 0 && physiqueCaisses.length > 0) {
                      // Default to first physical caisse if none selected
                      params.set('caisseIds', physiqueCaisses[0].id.toString());
                    }
                    
                    // Add date range from the date picker, or default to today if not set
                    if (dateRange?.start && dateRange?.end) {
                      params.set('fromDate', dateRange.start.toLocaleDateString('en-CA'));
                      params.set('toDate', dateRange.end.toLocaleDateString('en-CA'));
                    } else {
                      // Default to today if no date range is set (matches backend default)
                      const today = new Date();
                      params.set('fromDate', today.toLocaleDateString('en-CA'));
                      params.set('toDate', today.toLocaleDateString('en-CA'));
                    }
                    
                    history.push(`/transactions?${params.toString()}`);
                  }
                }}
                containerPadding={0}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: '300px', position: 'relative' }}>
            <Rows
              title=" "
              hideHeader={true}
              columns={[
                { 
                  key: 'createdAt', 
                  label: 'Date', 
                  sortable: true,
                  render: (value) => new Date(value).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })
                },
                { 
                  key: 'amount', 
                  label: 'Montant',
                  sortable: true,
                  render: (amount, row) => (
                    <span style={{ 
                      color: row.amountColor,
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      lineHeight: '19px',
                      letterSpacing: '0px',
                      textTransform: 'none',
                      opacity: 1
                    }}>
                      {amount}
                    </span>
                  )
                },
                { key: 'agentName', label: 'Par', sortable: true }
              ]}
              data={coffreTransactionsData}
              maxHeight="500px"
              sortBy={coffreSortBy}
              sortDirection={coffreSortDirection}
              onSort={(sortBy, sortDirection) => {
                setCoffreSortBy(sortBy);
                setCoffreSortDirection(sortDirection);
              }}
              containerPadding={0}
            />
          </Box>
        </Box>
      </Box>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Dashboard;

